import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Users, Mail, Lock, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinTeam() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!token) {
      toast.error('Invalid invite link');
      navigate('/');
      return;
    }
    
    // Fetch invite details to pre-fill name
    const fetchInviteDetails = async () => {
      try {
        const response = await axios.get(`${API}/team/invite/${token}`);
        setInviteDetails(response.data);
        setFormData(prev => ({ ...prev, name: response.data.name || '' }));
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Invalid or expired invite');
        navigate('/');
      } finally {
        setInviteLoading(false);
      }
    };
    
    fetchInviteDetails();
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/team/join`, {
        invite_token: token,
        name: formData.name,
        password: formData.password
      });
      
      // Save token and redirect
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      toast.success('Welcome to the team!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to join team';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (inviteLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF5F00] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading invite details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-hard w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#FF5F00] rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
            Join Team
          </h1>
          {inviteDetails?.business_name && (
            <p className="text-[#FF5F00] font-bold mb-1">{inviteDetails.business_name}</p>
          )}
          <p className="text-slate-500">
            Create your account to join the team
          </p>
          {inviteDetails?.email && (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Signing up as</p>
              <p className="font-medium text-slate-700">{inviteDetails.email}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
              Your Name
            </Label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className="h-12 pl-12 bg-white border-2 border-slate-300"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create password"
                className="h-12 pl-12 bg-white border-2 border-slate-300"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
              Confirm Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm password"
                className="h-12 pl-12 bg-white border-2 border-slate-300"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-[#FF5F00] hover:bg-orange-600 text-white font-bold uppercase tracking-wide text-lg mt-6"
          >
            {loading ? 'Joining...' : 'Join Team'}
          </Button>
        </form>

        <p className="text-center text-slate-500 text-sm mt-6">
          Already have an account?{' '}
          <a href="/login" className="text-[#FF5F00] font-bold hover:underline">
            Log in
          </a>
        </p>
      </div>
    </div>
  );
}
